import { useEffect, useMemo, useState } from "react";

import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { Checkbox } from "@/components/ui/checkbox";
import { Field, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import ListInput from "components/BaseForm/fields/ListInput";
import { defaultOptionsForInference, deriveFieldId } from "./helpers";

// base-ui Select items can't carry an empty-string value, so a sentinel stands
// in for "skip this column" / "use the default label field" and maps back.
const SKIP = "__skip__";
const LABEL_DEFAULT = "__default__";

const TYPE_OPTIONS = [
  { value: "short_text", label: "Short text" },
  { value: "text", label: "Long text" },
  { value: "number", label: "Number" },
  { value: "date", label: "Date" },
  { value: "boolean", label: "Boolean" },
  { value: "single_select", label: "Single select" },
  { value: "multi_select", label: "Multi select" },
  { value: "list", label: "List" },
  { value: "relationship", label: "Relationship" },
  { value: "email", label: "Email" },
  { value: "url", label: "URL" },
  { value: "phone", label: "Phone" },
  { value: "percentage", label: "Percentage" },
  { value: "currency", label: "Currency" },
];

const TYPE_LABEL_BY_VALUE = new Map(TYPE_OPTIONS.map(o => [o.value, o.label]));

const UNIQUE_DISALLOWED_TYPES = new Set(["boolean", "multi_select", "list"]);

// Types whose fit against the sampled values can be checked up front. Text
// always fits and select choices are built from the data, so only these
// warrant a "values won't coerce" warning.
const FIT_CHECKED_TYPE_LABELS = {
  date: "Date",
  number: "Number",
  boolean: "Boolean",
  email: "Email",
  url: "URL",
  phone: "Phone",
  percentage: "Percentage",
  currency: "Currency",
};

const typeFitWarningFor = row => {
  if (!row.include) return null;
  const label = FIT_CHECKED_TYPE_LABELS[row.type];
  if (!label) return null;
  const rate = row.type_match_rates?.[row.type];
  if (typeof rate !== "number" || rate >= 1) return null;
  const total = row.sampled_count ?? 0;
  if (total <= 0) return null;
  const mismatched = total - Math.round(rate * total);
  return (
    `About ${mismatched} of ${total} sampled values don't fit ${label} — ` +
    `those rows will be skipped at import.`
  );
};

const MappingRow = ({
  row,
  onChange,
  error,
  existingColumns = null,
  relationshipTargetOptions = [],
  labelFieldOptionsFor = () => [],
}) => {
  const isAppend = Boolean(row?.append_locked);
  const typeFitWarning = typeFitWarningFor(row);
  const [displayName, setDisplayName] = useState(row.display_name ?? "");
  const [fieldId, setFieldId] = useState(row.field_id ?? "");
  const [fieldIdManuallyEdited, setFieldIdManuallyEdited] = useState(false);

  useEffect(() => {
    setDisplayName(row.display_name ?? "");
    setFieldId(row.field_id ?? "");
  }, [row.csv_column_name, row.field_id, row.display_name]);

  const update = patch => {
    onChange({ ...row, ...patch });
  };

  const handleDisplayNameChange = e => {
    const next = e.target.value;
    setDisplayName(next);
    if (fieldIdManuallyEdited) {
      update({ display_name: next });
      return;
    }
    const nextFieldId = deriveFieldId(next);
    setFieldId(nextFieldId);
    update({
      display_name: next,
      field_id: nextFieldId,
    });
  };

  const handleFieldIdChange = e => {
    const next = e.target.value;
    setFieldId(next);
    setFieldIdManuallyEdited(true);
    update({ field_id: next });
  };

  const handleTypeChange = type => {
    // Re-derive options for the new type: inferred choices for selects,
    // nothing otherwise (relationship starts with no target picked).
    const patch = {
      type,
      options: defaultOptionsForInference({
        type,
        choices: row.choices,
        multi_select_choices: row.multi_select_choices,
      }),
    };
    if (UNIQUE_DISALLOWED_TYPES.has(type)) {
      patch.is_unique = false;
    }
    update(patch);
  };

  const handleTargetChange = targetType => {
    // Switching the target drops any label override — it belonged to the
    // previous table's columns.
    update({ options: targetType ? { target_type: targetType } : null });
  };

  const handleChoicesChange = choices => {
    // Drop options entirely when the last choice is removed so validateMapping
    // re-blocks the advance gate; otherwise merge into any existing options.
    update({
      options: choices.length ? { ...row.options, choices } : null,
    });
  };

  const handleLabelFieldChange = next => {
    const labelFieldId = next === LABEL_DEFAULT ? "" : next;
    update({
      options: {
        target_type: row.options?.target_type,
        ...(labelFieldId ? { label_field_id: labelFieldId } : {}),
      },
    });
  };

  const handleAppendFieldChange = next => {
    const nextFieldId = next === SKIP ? "" : next;
    if (!nextFieldId) {
      // "Skip this column" — drop the mapping and force include=false.
      update({
        field_id: null,
        include: false,
        display_name: row.csv_column_name,
        append_matched: false,
      });
      return;
    }
    const matched = (existingColumns ?? []).find(
      c => c.field_id === nextFieldId
    );
    update({
      include: true,
      field_id: matched?.field_id ?? nextFieldId,
      display_name: matched?.display_name ?? row.display_name,
      type: matched?.type ?? row.type,
      is_required: matched?.is_required ?? false,
      is_unique: matched?.is_unique ?? false,
      options: matched?.options ?? null,
      append_matched: Boolean(matched),
    });
  };

  const uniqueDisabled = UNIQUE_DISALLOWED_TYPES.has(row.type);
  const isSelectType =
    row.type === "single_select" || row.type === "multi_select";
  // Append mode: existing select columns are strict by default — ticking
  // this lets the worker extend ``options.choices`` with new values from
  // this CSV instead of skipping rows with novel values.
  const showAllowAdditions = isAppend && row.append_matched && isSelectType;
  // New-table selects need their choices defined here; append mode inherits
  // them from the existing column, so the editor only applies up front.
  const showChoicesEditor = !isAppend && isSelectType;
  // Append mode inherits relationship options from the existing column, so
  // the target picker only applies to new-table imports.
  const showRelationshipPicker = !isAppend && row.type === "relationship";
  const labelFieldOptions = showRelationshipPicker
    ? labelFieldOptionsFor(row.options?.target_type)
    : [];

  const uid = row.csv_column_name;
  const existingColumnLabel = useMemo(
    () =>
      new Map(
        (existingColumns ?? []).map(c => [
          c.field_id,
          `${c.display_name} (${c.field_id})`,
        ])
      ),
    [existingColumns]
  );
  const targetLabel = useMemo(
    () => new Map(relationshipTargetOptions.map(o => [o.value, o.label])),
    [relationshipTargetOptions]
  );
  const labelFieldLabel = new Map(
    labelFieldOptions.map(o => [o.value, o.label])
  );

  return (
    <div
      className={cn(
        "tw:grid tw:grid-cols-[4rem_minmax(8rem,12rem)_minmax(8rem,12rem)_minmax(6rem,10rem)_minmax(9rem,10rem)_6rem_5.5rem_minmax(8rem,1fr)] tw:items-center tw:gap-3 tw:px-4 tw:py-3 tw:border-b tw:border-border tw:bg-card",
        !row.include && !isAppend && "tw:opacity-55"
      )}
      data-include={row.include}
    >
      <div className="tw:flex tw:items-center">
        <Checkbox
          checked={row.include}
          onCheckedChange={c => update({ include: !!c })}
          aria-label={`Include ${row.csv_column_name}`}
          disabled={isAppend && !row.append_matched}
        />
      </div>
      <div className="tw:font-sans tw:text-foreground tw:font-medium tw:truncate">
        {row.csv_column_name}
        {isAppend && row.field_id === "id" && (
          <Badge
            variant="warning"
            className="tw:ml-1"
            aria-label="Used as the upsert key"
          >
            Update key
          </Badge>
        )}
      </div>
      <div className="tw:min-w-0">
        {isAppend ? (
          <span className="tw:text-muted-foreground tw:text-sm tw:italic">
            {displayName || "—"}
          </span>
        ) : (
          <Input
            value={displayName}
            onChange={handleDisplayNameChange}
            aria-label={`Display name for ${row.csv_column_name}`}
          />
        )}
      </div>
      {isAppend ? (
        <div className="tw:min-w-0">
          <Select
            value={row.field_id ?? null}
            onValueChange={handleAppendFieldChange}
          >
            <SelectTrigger
              aria-label={`Existing column for ${row.csv_column_name}`}
              className="tw:w-full"
            >
              <SelectValue placeholder="Select a column…">
                {value =>
                  value
                    ? (existingColumnLabel.get(value) ?? value)
                    : "Select a column…"
                }
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={SKIP}>Skip this column</SelectItem>
              {(existingColumns ?? []).map(c => (
                <SelectItem key={c.field_id} value={c.field_id}>
                  {c.display_name} ({c.field_id})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ) : (
        <div className="tw:min-w-0">
          <Input
            className="tw:font-mono tw:text-sm"
            value={fieldId}
            onChange={handleFieldIdChange}
            aria-label={`Field id for ${row.csv_column_name}`}
            spellCheck={false}
            autoCapitalize="off"
            autoCorrect="off"
          />
        </div>
      )}
      <div className="tw:min-w-0">
        {isAppend ? (
          <span className="tw:text-muted-foreground tw:text-sm tw:italic">
            {row.type ?? "—"}
          </span>
        ) : (
          <Select value={row.type} onValueChange={handleTypeChange}>
            <SelectTrigger
              aria-label={`Type for ${row.csv_column_name}`}
              className="tw:w-full"
            >
              <SelectValue placeholder="Type">
                {value => TYPE_LABEL_BY_VALUE.get(value) ?? value}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {TYPE_OPTIONS.map(opt => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>
      <FieldLabel className="tw:font-normal">
        <Checkbox
          checked={row.is_required}
          onCheckedChange={c => update({ is_required: !!c })}
          disabled={isAppend}
          aria-label={`Required for ${row.csv_column_name}`}
        />
        Required
      </FieldLabel>
      <FieldLabel className="tw:font-normal">
        <Checkbox
          checked={row.is_unique}
          disabled={uniqueDisabled || isAppend}
          onCheckedChange={c => update({ is_unique: !!c })}
          aria-label={`Unique for ${row.csv_column_name}`}
        />
        Unique
      </FieldLabel>
      {showAllowAdditions && (
        <FieldLabel className="tw:font-normal">
          <Checkbox
            checked={Boolean(row.allow_choice_additions)}
            onCheckedChange={c => update({ allow_choice_additions: !!c })}
            aria-label={`Allow new choices for ${row.csv_column_name}`}
          />
          Allow new choices
        </FieldLabel>
      )}
      <div className="tw:flex tw:gap-1.5 tw:flex-wrap tw:min-w-0 tw:overflow-hidden">
        {(row.samples ?? []).slice(0, 3).map((sample, i) => (
          <Badge key={i} variant="outline" code className="tw:max-w-48">
            <span className="tw:min-w-0 tw:truncate">{sample}</span>
          </Badge>
        ))}
      </div>
      {showRelationshipPicker && (
        <div className="tw:col-span-full tw:flex tw:flex-wrap tw:items-end tw:gap-3 tw:pt-1">
          <Field className="tw:w-56">
            <FieldLabel htmlFor={`links-to-${uid}`}>Links to</FieldLabel>
            <Select
              value={row.options?.target_type ?? null}
              onValueChange={handleTargetChange}
            >
              <SelectTrigger
                id={`links-to-${uid}`}
                aria-label={`Links to for ${row.csv_column_name}`}
                className="tw:w-full"
              >
                <SelectValue placeholder="Pick a record type…">
                  {value => targetLabel.get(value) ?? value}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {relationshipTargetOptions.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          {labelFieldOptions.length > 0 && (
            <Field className="tw:w-56">
              <FieldLabel htmlFor={`label-col-${uid}`}>
                Label column (optional)
              </FieldLabel>
              <Select
                value={row.options?.label_field_id ?? LABEL_DEFAULT}
                onValueChange={handleLabelFieldChange}
              >
                <SelectTrigger
                  id={`label-col-${uid}`}
                  aria-label={`Label column for ${row.csv_column_name}`}
                  className="tw:w-full"
                >
                  <SelectValue placeholder="First text column (default)">
                    {value =>
                      value === LABEL_DEFAULT
                        ? "First text column (default)"
                        : (labelFieldLabel.get(value) ?? value)
                    }
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={LABEL_DEFAULT}>
                    First text column (default)
                  </SelectItem>
                  {labelFieldOptions.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          )}
          <span className="tw:text-muted-foreground tw:text-xs tw:italic">
            CSV values must be record ids of the linked type. Rows pointing at
            missing records go to the error report.
          </span>
        </div>
      )}
      {showChoicesEditor && (
        <div className="tw:col-span-full tw:flex tw:flex-col tw:gap-1 tw:pt-1">
          <label
            htmlFor={`choices-${row.csv_column_name}`}
            className="tw:text-sm tw:text-muted-foreground"
          >
            Choices for {row.csv_column_name}
          </label>
          <ListInput
            id={`choices-${row.csv_column_name}`}
            value={row.options?.choices ?? []}
            onChange={handleChoicesChange}
            placeholder="Add a choice and press Enter"
          />
        </div>
      )}
      {error && (
        <div className="tw:col-span-full tw:text-warning tw:text-xs tw:pt-1">
          {error}
        </div>
      )}
      {typeFitWarning && (
        <div
          className="tw:col-span-full tw:mt-1 tw:px-3 tw:py-2 tw:rounded-sm tw:bg-accent tw:border-l-2 tw:border-l-primary tw:text-foreground tw:text-xs"
          role="status"
        >
          {typeFitWarning}
        </div>
      )}
      {isAppend && row.field_id === "id" && (
        <div className="tw:col-span-full tw:text-muted-foreground tw:text-xs tw:pt-1 tw:italic">
          CSV rows with this column filled in will update existing rows. Blank
          means a new row will be added.
        </div>
      )}
    </div>
  );
};

export default MappingRow;
